import { useStore } from '@nanostores/react';
import { AnimatePresence, motion } from 'framer-motion';
import { computed } from 'nanostores';
import { memo, useEffect, useRef, useState } from 'react';
import { createHighlighter, type BundledLanguage, type BundledTheme, type HighlighterGeneric } from 'shiki';
import type { ActionState } from '~/lib/runtime/action-runner';
import { workbenchStore } from '~/lib/stores/workbench';
import { classNames } from '~/utils/classNames';
import { cubicEasingFn } from '~/utils/easings';
import { CopyToClipboardButton } from '~/components/ui/CopyToClipboardButton';

const highlighterOptions = {
  langs: ['shell'],
  themes: ['light-plus', 'dark-plus'],
};

const shellHighlighter: HighlighterGeneric<BundledLanguage, BundledTheme> =
  import.meta.hot?.data.shellHighlighter ?? (await createHighlighter(highlighterOptions));

if (import.meta.hot) {
  import.meta.hot.data.shellHighlighter = shellHighlighter;
}

interface ArtifactProps {
  messageId: string;
  index: number;
}

export const Artifact = memo(({ messageId, index }: ArtifactProps) => {
  const userToggledActions = useRef(false);
  const [showActions, setShowActions] = useState(false);

  const artifacts = useStore(workbenchStore.artifacts);
  const artifact = artifacts[messageId];

  const actions = useStore(
    computed(artifact.runner.actions, (actions) => {
      return Object.values(actions);
    }),
  );

  const toggleActions = () => {
    userToggledActions.current = true;
    setShowActions(!showActions);
  };

  useEffect(() => {
    console.log('[refresh] index from Artifact: ', actions);

    if (actions.length && !showActions && !userToggledActions.current) {
      setShowActions(true);
    }
  }, [actions]);

  return (
    <div className="artifact border border-alpha-elements-borderColor flex flex-col overflow-hidden rounded-lg w-full transition-border duration-150">
      <div className="flex items-center justify-between bg-black">
        <button
          className="flex items-stretch bg-alpha-elements-artifacts-background hover:bg-alpha-elements-artifacts-backgroundHover w-full overflow-hidden"

          /*
           * onClick={() => {
           *   const showWorkbench = workbenchStore.showWorkbench.get();
           *   workbenchStore.showWorkbench.set(!showWorkbench);
           * }}
           */
        >
          <div className="px-5 p-3.5 w-full text-left">
            <div className="w-full text-alpha-elements-textPrimary font-medium leading-5 text-sm">
              {artifact?.title}
            </div>
            {index !== 1 && (
              <div className="w-full text-alpha-elements-textSecondary text-xs mt-0.5">
                Version {index - Math.floor(index / 2) - 1}
              </div>
            )}
          </div>
        </button>

        {/* restore button */}

        <div className="bg-alpha-elements-artifacts-borderColor w-[1px]" />
        <AnimatePresence>
          {actions.length && (
            <motion.button
              initial={{ width: 0 }}
              animate={{ width: 'auto' }}
              exit={{ width: 0 }}
              transition={{ duration: 0.15, ease: cubicEasingFn }}
              className="bg-alpha-elements-artifacts-background hover:bg-alpha-elements-artifacts-backgroundHover"
              onClick={toggleActions}
            >
              <div className="p-4">
                <div className={showActions ? 'i-ph:caret-up-bold' : 'i-ph:caret-down-bold'}></div>
              </div>
            </motion.button>
          )}
        </AnimatePresence>
      </div>
      <AnimatePresence>
        {showActions && actions.length > 0 && (
          <motion.div
            className="actions"
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            exit={{ height: '0px' }}
            transition={{ duration: 0.15 }}
          >
            <div className="bg-alpha-elements-artifacts-borderColor h-[1px]" />
            <div className="p-3 text-left bg-alpha-elements-actions-background">
              <FileList actions={actions} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

interface ShellCodeBlockProps {
  classsName?: string;
  code: string;
  disableCopy?: boolean;
}

function ShellCodeBlock({ classsName, code, disableCopy = false }: ShellCodeBlockProps) {
  return (
    <div className={classNames('text-xs relative font-mono bg-black group', classsName)}>
      <CopyToClipboardButton code={code} disabled={disableCopy} />
      <pre
        className="text-alpha-elements-textPrimary p-0 m-0 overflow-x-auto"
        style={{
          scrollbarWidth: 'thin',
          scrollbarColor: 'var(--alpha-background-tertiary) var(--alpha-background-primary)',
        }}
      >
        {code}
      </pre>
    </div>
  );
}

interface ActionListProps {
  actions: ActionState[];
}

const actionVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

// new component for displaying the file list
const FileList = memo(({ actions }: ActionListProps) => {
  // filter actions to only show file actions
  const fileActions = actions.filter((action) => action.type === 'file');
  const shellActions = actions.filter((action) => action.type === 'shell');

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
      <ul className="list-none space-y-2">
        {fileActions.map((action, index) => (
          <motion.li
            key={index}
            variants={actionVariants}
            initial="hidden"
            animate="visible"
            transition={{
              duration: 0.2,
              ease: cubicEasingFn,
            }}
            className="flex items-center gap-2 text-sm"
          >
            <div className={classNames('text-lg', getIconColor(action.status))}>
              {action.status === 'running' ? (
                <div className="i-svg-spinners:pulse-multiple"></div>
              ) : action.status === 'pending' ? (
                <div className="i-ph:circle-duotone"></div>
              ) : action.status === 'complete' ? (
                <div className="i-ph:check-circle-duotone"></div>
              ) : action.status === 'failed' || action.status === 'aborted' ? (
                <div className="i-ph:x"></div>
              ) : null}
            </div>

            <div className="text-alpha-elements-textPrimary w-48 break-words overflow-hidden line-clamp-6 line-height-4">
              {action.filePath}
            </div>
            <div className="ml-auto text-alpha-elements-textTertiary text-xs">Generated</div>
          </motion.li>
        ))}
      </ul>

      {shellActions.length > 0 && (
        <div className="mt-4">
          {shellActions.map((action, index) => (
            <motion.div
              key={`shell-${index}`}
              variants={actionVariants}
              initial="hidden"
              animate="visible"
              transition={{
                duration: 0.2,
                ease: cubicEasingFn,
              }}
              className="mb-2"
            >
              <div className="border border-alpha-elements-borderColor rounded-md overflow-hidden bg-[#1E1E1E]">
                <div className="px-3 py-1 bg-[#252526] border-b border-alpha-elements-borderColor flex items-center gap-2">
                  <div className="flex items-center gap-2 text-xs text-alpha-elements-textTertiary mb-1">
                    <div className={classNames('text-base', getIconColor(action.status))}>
                      {action.status === 'running' ? (
                        <div className="i-svg-spinners:pulse-multiple text-sm"></div>
                      ) : action.status === 'pending' ? (
                        <div className="i-ph:circle-duotone"></div>
                      ) : action.status === 'complete' ? (
                        <div className="i-ph:check-circle-duotone"></div>
                      ) : action.status === 'failed' || action.status === 'aborted' ? (
                        <div className="i-ph:x"></div>
                      ) : null}
                    </div>
                    <div className="text-alpha-elements-textSecondary font-medium">terminal</div>
                  </div>
                  <div className="ml-auto text-xs text-alpha-elements-textSecondary">
                    {action.status === 'running'
                      ? 'Running...'
                      : action.status === 'complete'
                        ? 'Completed'
                        : action.status === 'failed'
                          ? 'Failed'
                          : action.status === 'aborted'
                            ? 'Aborted'
                            : 'Pending'}
                  </div>
                </div>
                <div className="p-1">
                  <ShellCodeBlock
                    classsName="rounded-md overflow-hidden p-0 pl-2 border-l-2 border-l-alpha-elements-loader-progress"
                    code={action.content}
                  />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </motion.div>
  );
});

// original ActionList component kept for reference
const ActionList = memo(({ actions }: ActionListProps) => {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
      <ul className="list-none space-y-2.5">
        {actions.map((action, index) => {
          const { status, type, content } = action;
          const isLast = index === actions.length - 1;

          return (
            <motion.li
              key={index}
              variants={actionVariants}
              initial="hidden"
              animate="visible"
              transition={{
                duration: 0.2,
                ease: cubicEasingFn,
              }}
            >
              <div className="flex items-center gap-1.5 text-sm">
                <div className={classNames('text-lg', getIconColor(action.status))}>
                  {status === 'running' ? (
                    <div className="i-svg-spinners:90-ring-with-bg"></div>
                  ) : status === 'pending' ? (
                    <div className="i-ph:circle-duotone"></div>
                  ) : status === 'complete' ? (
                    <div className="i-ph:check"></div>
                  ) : status === 'failed' || status === 'aborted' ? (
                    <div className="i-ph:x"></div>
                  ) : null}
                </div>
                {type === 'file' ? (
                  <div>
                    Create{' '}
                    <code className="bg-alpha-elements-artifacts-inlineCode-background text-alpha-elements-artifacts-inlineCode-text px-1.5 py-1 rounded-md">
                      {action.filePath}
                    </code>
                  </div>
                ) : type === 'shell' ? (
                  <div className="flex items-center w-full min-h-[28px]">
                    <span className="flex-1">Run command</span>
                  </div>
                ) : null}
              </div>
              {type === 'shell' && (
                <ShellCodeBlock
                  classsName={classNames('mt-1', {
                    'mb-3.5': !isLast,
                  })}
                  code={content}
                />
              )}
            </motion.li>
          );
        })}
      </ul>
    </motion.div>
  );
});

function getIconColor(status: ActionState['status']) {
  switch (status) {
    case 'pending': {
      return 'text-alpha-elements-textTertiary';
    }
    case 'running': {
      return 'text-alpha-elements-loader-progress';
    }
    case 'complete': {
      return 'text-alpha-elements-icon-success';
    }
    case 'aborted': {
      return 'text-alpha-elements-textSecondary';
    }
    case 'failed': {
      return 'text-alpha-elements-icon-error';
    }
    default: {
      return undefined;
    }
  }
}
